import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  IconArrowLeft,
  Currency,
  Response,
  Table,
  formatBalance,
  IconSelected,
  ButtonSize,
  IconBlock,
  ButtonSecondary,
  Popup,
  ButtonPrimary,
  ButtonPseudo,
  ResponseError,
} from '@ui/src';
import { useAppSelector } from '@store';
import { Balance, CustomerStatus } from '../types';
import { useChangeBalanceStatusMutation } from '../store/services/customers';
import { useLazyGetIndividualAccountBalanceQuery } from '../../individual/store/services/individual';
import {
  currenciesColumns,
  currenciesColumnsLabels,
} from './variables/currencies';
import { AccountCurrenciesTypes } from './types';

import cn from './account-currencies.module.css';

interface CurrencyItem extends Currency {
  render?: Record<string, React.JSX.Element>;
}

const formatCurrencies = (
  currencies: CurrencyItem[],
  balances: Balance[],
  setBlockPopupVisible: (data: boolean) => void,
  setSelectedBlockCurrency: (data: Currency) => void,
  setEnablePopupVisible: (data: boolean) => void,
  setSelectedEnableCurrency: (data: Currency) => void,
  status?: CustomerStatus,
) => {
  const nextList = currencies?.map((item) => {
    const result = { ...item };

    const currentBalance = balances?.find(
      (balance) => balance.currencyId === item.id,
    );

    const render: Record<string, React.JSX.Element> = {
      name: (
        <div className={!currentBalance ? cn.disabledBalance : ''}>
          {result.name}
        </div>
      ),
      description: (
        <div className={!currentBalance ? cn.disabledBalance : ''}>
          {result.description}
        </div>
      ),
      decimals: (
        <div className={!currentBalance ? cn.disabledBalance : ''}>
          {currentBalance
            ? formatBalance(currentBalance?.value || 0, item)
            : '-'}
        </div>
      ),
      code: (
        <div
          className={`${cn.availableRow} ${
            !currentBalance ? cn.disabledBalance : ''
          }`}
        >
          <div>{currentBalance ? 'Available' : 'Unavailable'}</div>
          <ButtonSecondary
            className={`${cn.activateButton} button-white`}
            text={currentBalance ? 'Disable' : 'Enable'}
            icon={
              currentBalance ? (
                <IconBlock className={cn.blockIcon} />
              ) : (
                <IconSelected className={cn.enableIcon} />
              )
            }
            size={ButtonSize.SMALL}
            onClick={() => {
              if (currentBalance) {
                setBlockPopupVisible(true);
                setSelectedBlockCurrency(item);
              } else {
                setEnablePopupVisible(true);
                setSelectedEnableCurrency(item);
              }
            }}
            disabled={
              Boolean(currentBalance && currentBalance?.value) ||
              status === CustomerStatus.CLOSED
            }
          />
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const AccountCurrencies = ({
  setShowCurrencies,
  setSelectedAccountCurrenciesId,
  accounts,
  selectedAccountCurrenciesId,
  status,
}: AccountCurrenciesTypes) => {
  const [balances, setBalances] = useState<Balance[]>([]);
  const [blockPopupVisible, setBlockPopupVisible] = useState(false);
  const [enablePopupVisible, setEnablePopupVisible] = useState(false);
  const [selectedBlockCurrency, setSelectedBlockCurrency] = useState<
    Currency | undefined
  >(undefined);
  const [selectedEnableCurrency, setSelectedEnableCurrency] = useState<
    Currency | undefined
  >(undefined);
  const [error, setError] = useState('');

  const { currenciesList } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const [onEnableBalance, { isLoading: enableInProgress }] =
    useChangeBalanceStatusMutation();
  const [onDisableBalance, { isLoading: disableInProgress }] =
    useChangeBalanceStatusMutation();

  const formattedData = useMemo(() => {
    const data = formatCurrencies(
      currenciesList as Currency[],
      balances,
      setBlockPopupVisible,
      setSelectedBlockCurrency,
      setEnablePopupVisible,
      setSelectedEnableCurrency,
      status,
    );

    return data;
  }, [currenciesList, balances, status]);

  const onEnableBalanceAction = async () => {
    const response = (await onEnableBalance({
      id: selectedAccountCurrenciesId as string,
      body: {
        currencyId: selectedEnableCurrency?.id as string,
        active: true,
      },
    })) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: Balance };
    };

    if (response?.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setError(response?.error?.data?.message[0]?.value as string);
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setError(response?.error?.data?.message);
      } else {
        setError('Internal Server Error. Try Again');
      }
    } else if (response?.data && response?.data?.data) {
      const nextBalances = ([] as Balance[]).concat(balances);
      nextBalances.push(response.data.data);

      setBalances(nextBalances);
      setSelectedEnableCurrency(undefined);
      setEnablePopupVisible(false);
    }
  };

  const onDisableBalanceAction = async () => {
    const response = (await onDisableBalance({
      id: selectedAccountCurrenciesId as string,
      body: {
        currencyId: selectedBlockCurrency?.id as string,
        active: false,
      },
    })) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: Balance };
    };

    if (response?.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setError(response?.error?.data?.message[0]?.value as string);
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setError(response?.error?.data?.message);
      } else {
        setError('Internal Server Error. Try Again');
      }
    } else if (response?.data && response?.data?.data) {
      setBalances(
        balances?.filter((item) => item.id !== response?.data?.data?.id),
      );
      setSelectedBlockCurrency(undefined);
      setBlockPopupVisible(false);
    }
  };

  const [getBalance, { isLoading, isFetching }] =
    useLazyGetIndividualAccountBalanceQuery();

  const getBalancesAction = useCallback(async (id: string) => {
    const response = (await getBalance(id)) as { data: Response<Balance> };

    if (response && response?.data?.data) {
      setBalances(response?.data?.data?.edges?.map((item) => item.node));
    }
  }, []);

  useEffect(() => {
    if (selectedAccountCurrenciesId) {
      getBalancesAction(selectedAccountCurrenciesId);
    }
  }, [selectedAccountCurrenciesId]);

  return (
    <div className={cn.availableCurrencies}>
      <button
        type="button"
        className={cn.backButton}
        onClick={() => {
          setShowCurrencies(false);
          setSelectedAccountCurrenciesId('');
        }}
      >
        <IconArrowLeft width={13} height={11} />
        Back to Accounts
      </button>
      <div className={cn.availableCurrenciesTitle}>Available Currencies</div>
      <div className={cn.accountsWrapper}>
        {accounts?.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`${cn.accountButton} ${
              selectedAccountCurrenciesId === item.id
                ? cn.accountButtonActive
                : ''
            }`}
            onClick={() => setSelectedAccountCurrenciesId(item.id)}
          >
            {item.fullNumber}
          </button>
        ))}
      </div>
      <div className={cn.currenciesTableWrapper}>
        <Table
          columns={currenciesColumns}
          data={formattedData || []}
          sort={{}}
          onSort={() => {}}
          filters={{}}
          columnsLabels={currenciesColumnsLabels}
          tableBodyClassName={`${cn.currenciesTable} ${
            isLoading || isFetching ? cn.currenciesTableLoading : ''
          }`}
          onFilterChange={() => {}}
          isLoading={isLoading || isFetching}
        />
      </div>
      <Popup
        title={`Disable the balance in ${
          currenciesList?.find((item) => item.id === selectedBlockCurrency?.id)
            ?.name
        } for the customer?`}
        onCancelClick={() => {
          setBlockPopupVisible(false);
          setSelectedBlockCurrency(undefined);
          setError('');
        }}
        isVisible={blockPopupVisible}
      >
        <>
          {error && <div className={cn.error}>{error}</div>}
          <div className={cn.controls}>
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setBlockPopupVisible(false);
                setSelectedBlockCurrency(undefined);
                setError('');
              }}
              className={cn.closeButton}
              bold
            />
            <ButtonPrimary
              text={disableInProgress ? 'Loading...' : 'Disable'}
              icon={<IconBlock className={cn.okButtonBlock} />}
              onClick={onDisableBalanceAction}
              className={cn.okButton}
              bold
            />
          </div>
        </>
      </Popup>
      <Popup
        title={`Enable the balance in ${
          currenciesList?.find((item) => item.id === selectedEnableCurrency?.id)
            ?.name
        } for the customer?`}
        onCancelClick={() => {
          setEnablePopupVisible(false);
          setSelectedEnableCurrency(undefined);
          setError('');
        }}
        isVisible={enablePopupVisible}
      >
        <>
          {error && <div className={cn.error}>{error}</div>}
          <div className={cn.controls}>
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setEnablePopupVisible(false);
                setSelectedEnableCurrency(undefined);
                setError('');
              }}
              className={cn.closeButton}
              bold
            />
            <ButtonPrimary
              text={enableInProgress ? 'Loading...' : 'Enable'}
              icon={<IconSelected width={18} height={18} />}
              onClick={onEnableBalanceAction}
              disabled={enableInProgress}
              className={cn.okButton}
              bold
            />
          </div>
        </>
      </Popup>
    </div>
  );
};

export default AccountCurrencies;
